<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';

const props = defineProps({
  scope: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:scope']);

const { t } = useI18n();

const modeOptions = computed(() => [
  { value: 'all', label: t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.ALL') },
  {
    value: 'resolved_only',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.RESOLVED_ONLY'),
  },
  {
    value: 'selected',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.SELECTED'),
  },
  {
    value: 'date_range',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.DATE_RANGE'),
  },
]);

const mode = computed(() => props.scope.mode || 'all');

const conversationDisplayIdsText = computed(() =>
  Array.isArray(props.scope.conversation_display_ids)
    ? props.scope.conversation_display_ids.join(', ')
    : ''
);

const updateScope = patch => {
  emit('update:scope', { ...props.scope, ...patch });
};

const onModeChange = event => {
  updateScope({ mode: event.target.value });
};

const onDisplayIdsInput = event => {
  const ids = event.target.value
    .split(/[\s,;]+/)
    .map(id => id.trim())
    .filter(Boolean)
    .map(id => Number.parseInt(id, 10))
    .filter(Number.isFinite);
  updateScope({ conversation_display_ids: ids });
};

const onCreatedAfterInput = event => {
  updateScope({ created_after: event.target.value || null });
};

const onCreatedBeforeInput = event => {
  updateScope({ created_before: event.target.value || null });
};
</script>

<template>
  <SettingsFieldSection
    :label="$t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.TITLE')"
    :help-text="$t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.DESCRIPTION')"
    class="[&>div]:!items-start"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label
          v-for="option in modeOptions"
          :key="option.value"
          class="flex gap-2 items-center text-sm text-n-slate-12 cursor-pointer"
        >
          <input
            type="radio"
            name="transfer-scope"
            :value="option.value"
            :checked="mode === option.value"
            class="m-0 flex-shrink-0 accent-n-brand"
            @change="onModeChange"
          />
          <span>{{ option.label }}</span>
        </label>
      </div>
      <div v-if="mode === 'selected'">
        <label
          for="transfer-conversation-ids"
          class="block mb-1 text-sm font-medium text-n-slate-12"
        >
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.SELECTED') }}
        </label>
        <textarea
          id="transfer-conversation-ids"
          :value="conversationDisplayIdsText"
          rows="3"
          class="!mb-0"
          :placeholder="
            $t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.SELECTED_PLACEHOLDER')
          "
          @input="onDisplayIdsInput"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.SELECTED_HELP') }}
        </p>
      </div>

      <div
        v-if="mode === 'date_range'"
        class="grid grid-cols-1 gap-3 sm:grid-cols-2"
      >
        <div>
          <label
            for="transfer-created-after"
            class="block mb-1 text-sm font-medium text-n-slate-12"
          >
            {{
              $t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.CREATED_AFTER_LABEL')
            }}
          </label>
          <input
            id="transfer-created-after"
            type="date"
            :value="props.scope.created_after || ''"
            class="!mb-0"
            @input="onCreatedAfterInput"
          />
        </div>
        <div>
          <label
            for="transfer-created-before"
            class="block mb-1 text-sm font-medium text-n-slate-12"
          >
            {{
              $t('INBOX_MGMT.CONVERSATION_TRANSFER.SCOPE.CREATED_BEFORE_LABEL')
            }}
          </label>
          <input
            id="transfer-created-before"
            type="date"
            :value="props.scope.created_before || ''"
            class="!mb-0"
            @input="onCreatedBeforeInput"
          />
        </div>
      </div>
    </div>
  </SettingsFieldSection>
</template>
